import React, { useState } from 'react';
import styled from 'styled-components';
import { useEditorStore } from '../../store/editorStore';
import { PlotModal } from './PlotModal';
import { DataFrameModal } from './DataFrameModal';
import glassTheme from '../../styles/glassTheme';

const OutputContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-width: 0;
  min-height: 0;
  ${glassTheme.glass.light}
  overflow: hidden;
`;

const OutputHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  min-height: 56px;
  background: rgba(0, 0, 0, 0.3);
  border-bottom: 1px solid ${glassTheme.colors.border};
`;

const OutputTitle = styled.span`
  color: ${glassTheme.colors.text.primary};
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
`;

const OutputStatus = styled.span<{ type?: 'success' | 'error' | 'running' }>`
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${props => {
    switch (props.type) {
      case 'success':
        return 'rgba(74, 222, 128, 0.2)';
      case 'error':
        return 'rgba(248, 113, 113, 0.2)';
      case 'running':
        return 'rgba(96, 165, 250, 0.2)';
      default:
        return 'rgba(255, 255, 255, 0.1)';
    }
  }};
  color: ${props => {
    switch (props.type) {
      case 'success':
        return '#4ade80';
      case 'error':
        return '#f87171';
      case 'running':
        return '#60a5fa';
      default:
        return 'rgba(255, 255, 255, 0.8)';
    }
  }};
`;

const OutputContent = styled.div`
  flex: 1;
  padding: 1rem;
  overflow-y: auto;
  overflow-x: auto;
  font-family: 'Fira Code', 'Courier New', monospace;
  font-size: 0.9rem;
  line-height: 1.5;
  color: #00ff00;
  white-space: pre-wrap;
  background: rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-height: 0;

  &::-webkit-scrollbar {
    width: 12px;
    height: 12px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.3);
    border-radius: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(102, 126, 234, 0.5);
    border-radius: 6px;
    border: 2px solid rgba(0, 0, 0, 0.3);

    &:hover {
      background: rgba(102, 126, 234, 0.7);
    }
  }

  &::-webkit-scrollbar-corner {
    background: rgba(0, 0, 0, 0.3);
  }
`;

const OutputText = styled.pre`
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
`;

const PlotsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const PlotWrapper = styled.div`
  background: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  padding: 0.75rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const PlotImage = styled.img`
  width: 100%;
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  display: block;
`;

const PlotHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
`;

const PlotTitle = styled.span`
  color: #333;
  font-size: 0.85rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:before {
    content: '📊';
  }
`;

const PlotExpandButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 8px;
  padding: 0.4rem 0.8rem;
  color: white;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.3rem;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(102, 126, 234, 0.4);
  }

  &:after {
    content: '⛶';
    font-size: 0.7rem;
  }
`;

const ClearButton = styled.button`
  padding: 0.25rem 0.75rem;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 12px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    color: white;
  }
`;

const ExecutionHistory = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
`;

const ExecutionStep = styled.div<{ $hasError?: boolean }>`
  background: ${props => props.$hasError
    ? 'rgba(248, 113, 113, 0.1)'
    : 'rgba(102, 126, 234, 0.05)'};
  border: 1px solid ${props => props.$hasError
    ? 'rgba(248, 113, 113, 0.3)'
    : 'rgba(102, 126, 234, 0.2)'};
  border-radius: 12px;
  padding: 1rem;
  animation: slideIn 0.3s ease;
  overflow: hidden;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateX(-10px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;

const StepCode = styled.div`
  font-family: 'Fira Code', 'Courier New', monospace;
  font-size: 0.85rem;
  color: #ffd700;
  margin-bottom: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 6px;
  border-left: 3px solid #667eea;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;

  /* R syntax highlighting */
  .r-keyword {
    color: #569cd6;
    font-weight: bold;
  }
  .r-function {
    color: #dcdcaa;
  }
  .r-string {
    color: #ce9178;
  }
  .r-number {
    color: #b5cea8;
  }
  .r-comment {
    color: #6a9955;
    font-style: italic;
  }
  .r-operator {
    color: #d4d4d4;
  }
  .r-bracket {
    color: #ffd700;
  }
  .r-assignment {
    color: #c586c0;
    font-weight: bold;
  }
`;

const StepOutput = styled.div<{ $hasError?: boolean }>`
  font-family: 'Fira Code', 'Courier New', monospace;
  font-size: 0.9rem;
  color: ${props => props.$hasError ? '#f87171' : '#00ff00'};
  white-space: pre-wrap;
  word-break: break-word;
  overflow-x: auto;

  &:empty:before {
    content: '(无输出)';
    color: rgba(255, 255, 255, 0.4);
    font-style: italic;
  }
`;

const StepPlots = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const StepPlotWrapper = styled.div`
  background: rgba(255, 255, 255, 0.95);
  border-radius: 8px;
  padding: 0.5rem;
  display: inline-block;
`;

const StepPlotImage = styled.img`
  max-width: 100%;
  height: auto;
  border-radius: 6px;
  max-height: 200px;
`;

const Spinner = styled.div`
  border: 2px solid rgba(96, 165, 250, 0.3);
  border-radius: 50%;
  border-top: 2px solid #60a5fa;
  width: 12px;
  height: 12px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const OutputFooter = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.3);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const OutputFooterButton = styled.button<{ $variant?: 'dataframe' | 'plot' }>`
  padding: 0.5rem 1rem;
  background: ${props => props.$variant === 'dataframe'
    ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  border: none;
  border-radius: 20px;
  color: white;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(102, 126, 234, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

interface OutputPanelProps {
  height?: string;
}

/**
 * Component to render highlighted R code
 */
const HighlightedCode: React.FC<{ code: string }> = ({ code }) => {
  if (!code) return null;

  // Escape HTML entities FIRST, before anything else
  let escaped = code
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Use a token-based approach to avoid matching our own HTML tags
  const tokens: Array<{ text: string; type?: string }> = [];

  // Split by HTML entity boundaries and word boundaries
  let remaining = escaped;
  let pos = 0;

  while (pos < remaining.length) {
    // Match different token types in priority order
    const patterns = [
      // Assignment operators
      { regex: /&lt;-|-&gt;/, type: 'assignment' },
      // Keywords
      { regex: /\b(if|else|for|in|while|repeat|break|next|function|return|TRUE|FALSE|NULL|NA|Inf|NaN|library|require|source)\b/, type: 'keyword' },
      // Numbers
      { regex: /\b(\d+\.?\d*)\b/, type: 'number' },
      // Strings
      { regex: /(&quot;[^&]*&quot;|&apos;[^&]*&apos;)/, type: 'string' },
      // Comments
      { regex: /(#.*)/, type: 'comment' },
      // Function calls (word followed by opening paren)
      { regex: /\b([a-zA-Z_][a-zA-Z0-9._]*)\s*\(/, type: 'function' },
      // Brackets
      { regex: /([[\](){}])/, type: 'bracket' },
      // Operators (excluding & to avoid &amp;)
      { regex: /([+*^|!~=])/, type: 'operator' },
    ];

    let bestMatch: { end: number; text: string; type?: string } | null = null;

    for (const pattern of patterns) {
      const regex = new RegExp(pattern.regex.source, pattern.regex.flags);
      regex.lastIndex = pos;
      const match = regex.exec(remaining);
      if (match && match.index === pos) {
        bestMatch = {
          end: pos + match[0].length,
          text: match[0],
          type: pattern.type
        };
        break;
      } else if (match && match.index > pos) {
        // Found a match ahead, check if it's closer than current best
        if (!bestMatch || match.index < bestMatch.end) {
          bestMatch = {
            end: match.index + match[0].length,
            text: match[0],
            type: pattern.type
          };
        }
      }
    }

    if (bestMatch && bestMatch.end === pos + bestMatch.text.length && bestMatch.end <= remaining.length) {
      // Token starts at current position
      tokens.push({ text: bestMatch.text, type: bestMatch.type });
      pos = bestMatch.end;
    } else if (bestMatch && bestMatch.end > pos) {
      // Text before the token
      tokens.push({ text: remaining.slice(pos, bestMatch.end - bestMatch.text.length) });
      tokens.push({ text: bestMatch.text, type: bestMatch.type });
      pos = bestMatch.end;
    } else {
      // No match found, add remaining and break
      tokens.push({ text: remaining.slice(pos) });
      break;
    }
  }

  // Build HTML from tokens
  const html = tokens.map(token => {
    if (token.type) {
      return `<span class="r-${token.type}">${token.text}</span>`;
    }
    return token.text;
  }).join('');

  return <span dangerouslySetInnerHTML={{ __html: html }} />;
};

export const OutputPanel: React.FC<OutputPanelProps> = ({ height = '100%' }) => {
  const {
    output, isRunning, error, clearOutput, plots, currentPlotIndex,
    dataFrames, currentDataFrameIndex, markPlotAsSeen, markDataFrameAsSeen,
    setCurrentDataFrame, setCurrentPlot, executionHistory
  } = useEditorStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDataFrameModalOpen, setIsDataFrameModalOpen] = useState(false);
  const [wasCleared, setWasCleared] = useState(false);

  const getStatus = () => {
    if (isRunning) return { type: 'running' as const, text: '运行中...', icon: <Spinner /> };
    if (error) return { type: 'error' as const, text: '运行失败', icon: '❌' };
    if (output && output !== '欢迎使用R语言编程环境！\n点击"运行"按钮执行您的代码。') {
      return { type: 'success' as const, text: '运行完成', icon: '✅' };
    }
    return { type: undefined, text: '就绪', icon: '🟢' };
  };

  const status = getStatus();

  const handleOpenPlotModal = () => {
    if (plots.length > 0) {
      markPlotAsSeen();
      setCurrentPlot(0);
      setIsModalOpen(true);
    }
  };

  const handleNextPlot = () => {
    if (currentPlotIndex < plots.length - 1) {
      setCurrentPlot(currentPlotIndex + 1);
    }
  };

  const handlePreviousPlot = () => {
    if (currentPlotIndex > 0) {
      setCurrentPlot(currentPlotIndex - 1);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleOpenDataFrameModal = () => {
    if (dataFrames.length > 0) {
      markDataFrameAsSeen();
      setIsDataFrameModalOpen(true);
    }
  };

  const handleNextDataFrame = () => {
    if (currentDataFrameIndex < dataFrames.length - 1) {
      setCurrentDataFrame(currentDataFrameIndex + 1);
    }
  };

  const handlePreviousDataFrame = () => {
    if (currentDataFrameIndex > 0) {
      setCurrentDataFrame(currentDataFrameIndex - 1);
    }
  };

  const handleCloseDataFrameModal = () => {
    setIsDataFrameModalOpen(false);
  };

  return (
    <OutputContainer style={{ height }} as="section" aria-labelledby="r-output-title">
      <OutputHeader>
        <OutputTitle id="r-output-title">
          <i className="fas fa-chart-line"></i>
          输出结果
        </OutputTitle>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <OutputStatus type={status.type}>
            {status.icon}
            {status.text}
          </OutputStatus>
          <ClearButton
            onClick={() => {
              clearOutput();
              setWasCleared(true);
            }}
            disabled={isRunning}
          >
            <i className="fas fa-eraser"></i>
            清空
          </ClearButton>
        </div>
      </OutputHeader>
      <OutputContent aria-busy={isRunning} aria-live="polite">
        {error ? (
          <div role="alert" style={{ color: glassTheme.colors.status.error }}>
            <strong>运行失败：</strong>{error}
          </div>
        ) : executionHistory.length > 0 ? (
          <ExecutionHistory>
            {executionHistory.map((step, index) => (
              <ExecutionStep key={step.timestamp || index} $hasError={!!step.error}>
                {step.code && (
                  <StepCode>
                    <HighlightedCode code={step.code} />
                  </StepCode>
                )}
                {step.output && (
                  <StepOutput $hasError={false}>{step.output}</StepOutput>
                )}
                {step.error && (
                  <StepOutput $hasError={true}>{step.error}</StepOutput>
                )}
                {step.plots && step.plots.length > 0 && (
                  <StepPlots>
                    {step.plots.map((plot, plotIndex) => (
                      <StepPlotWrapper key={plotIndex}>
                        <StepPlotImage
                          src={plot}
                          alt={`Step ${index + 1} Plot ${plotIndex + 1}`}
                          onClick={() => {
                            setCurrentPlot(plots.indexOf(plot));
                            handleOpenPlotModal();
                          }}
                          style={{ cursor: 'pointer' }}
                        />
                      </StepPlotWrapper>
                    ))}
                  </StepPlots>
                )}
              </ExecutionStep>
            ))}
          </ExecutionHistory>
        ) : (
          <>
            {output && output !== '欢迎使用R语言编程环境！\n点击"运行"按钮执行您的代码。' ? (
              <OutputText>
                {output}
              </OutputText>
            ) : (
              <OutputText>
                {wasCleared
                  ? '输出已清空，运行代码后将在这里显示结果。'
                  : '尚未运行代码。点击“运行”或按 Ctrl+Enter 开始。'}
              </OutputText>
            )}
            {plots && plots.length > 0 && (
              <PlotsContainer>
                {plots.map((plot, index) => (
                  <PlotWrapper key={index}>
                    <PlotHeader>
                      <PlotTitle>图表 {index + 1}</PlotTitle>
                      <PlotExpandButton onClick={() => {
                        setCurrentPlot(index);
                        handleOpenPlotModal();
                      }}>
                        全屏查看
                      </PlotExpandButton>
                    </PlotHeader>
                    <PlotImage
                      src={plot}
                      alt={`R Plot ${index + 1}`}
                      onClick={() => {
                        setCurrentPlot(index);
                        handleOpenPlotModal();
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                  </PlotWrapper>
                ))}
              </PlotsContainer>
            )}
          </>
        )}
      </OutputContent>
      <OutputFooter>
        {dataFrames.length > 0 && (
          <OutputFooterButton $variant="dataframe" onClick={handleOpenDataFrameModal}>
            <i className="fas fa-table"></i>
            查看数据表 ({dataFrames.length})
          </OutputFooterButton>
        )}
        {plots.length > 0 && (
          <OutputFooterButton $variant="plot" onClick={handleOpenPlotModal}>
            <i className="fas fa-chart-bar"></i>
            查看图表 ({plots.length})
          </OutputFooterButton>
        )}
      </OutputFooter>

      <PlotModal
        isOpen={isModalOpen}
        plots={plots}
        currentIndex={currentPlotIndex}
        onClose={handleCloseModal}
        onNext={handleNextPlot}
        onPrevious={handlePreviousPlot}
      />
      <DataFrameModal
        isOpen={isDataFrameModalOpen}
        dataFrames={dataFrames}
        currentIndex={currentDataFrameIndex}
        onClose={handleCloseDataFrameModal}
        onNext={handleNextDataFrame}
        onPrevious={handlePreviousDataFrame}
      />
    </OutputContainer>
  );
};